"use client";

import { useEffect, useState, useRef } from "react";
import { useSession } from "next-auth/react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Send, Image as ImageIcon, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import { cn } from "@/lib/utils";

type Message = {
    id: string;
    content: string;
    createdAt: string;
    senderId: string;
    sender: {
        name: string | null;
        image: string | null;
    };
};

type ChatWindowProps = {
    otherUserId: string;
    listingId?: string | null;
};

export function ChatWindow({ otherUserId, listingId }: ChatWindowProps) {
    const { data: session } = useSession();
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [messageText, setMessageText] = useState("");
    const bottomRef = useRef<HTMLDivElement>(null);
    const [otherUser, setOtherUser] = useState<{ name: string, image: string } | null>(null);

    const currentUser = session?.user;

    const fetchMessages = async () => {
        try {
            const params = new URLSearchParams();
            params.set("otherUserId", otherUserId);
            if (listingId) params.set("listingId", listingId);

            const res = await fetch(`/api/messages?${params.toString()}`);
            if (!res.ok) throw new Error("Erreur chargement");
            const data = await res.json();

            // Deduplicate? For now just replace
            if (Array.isArray(data)) {
                setMessages(data);
                // Try to grab other user info from a received message if we don't have it
                if (!otherUser && data.length > 0) {
                    const match = data.find((m: Message) => m.senderId === otherUserId);
                    if (match) {
                        setOtherUser({ name: match.sender.name || "", image: match.sender.image || "" });
                    }
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch + Periodic poll
    useEffect(() => {
        setLoading(true);
        fetchMessages();
        const interval = setInterval(fetchMessages, 5000); // 5s polling for MVP
        return () => clearInterval(interval);
    }, [otherUserId, listingId]);

    // Scroll to bottom
    useEffect(() => {
        if (messages.length > 0) {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!messageText.trim() || sending) return;

        setSending(true);
        // Optimistic update
        const tempId = "temp-" + Date.now();
        const tempMsg: Message = {
            id: tempId,
            content: messageText,
            createdAt: new Date().toISOString(),
            senderId: currentUser?.id || "",
            sender: {
                name: currentUser?.name || null,
                image: currentUser?.image || null
            }
        };

        setMessages(prev => [...prev, tempMsg]);
        const textToSend = messageText;
        setMessageText("");

        try {
            const res = await fetch("/api/messages", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    content: textToSend,
                    receiverId: otherUserId,
                    listingId: listingId
                })
            });

            if (!res.ok) throw new Error("Echec envoi");

            // Refresh to get real ID and server timestamp
            await fetchMessages();
        } catch (err) {
            toast.error("Erreur lors de l'envoi");
            // Revert on error (simple way: remove temp)
            setMessages(prev => prev.filter(m => m.id !== tempId));
            setMessageText(textToSend);
        } finally {
            setSending(false);
        }
    };

    if (loading && messages.length === 0) {
        return <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="animate-spin mr-2" /> Chargement...</div>;
    }

    return (
        <div className="flex flex-col h-full bg-background rounded-lg ">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between bg-muted/20">
                <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                        <AvatarImage src={otherUser?.image || ""} />
                        <AvatarFallback>{otherUser?.name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div>
                        <h3 className="font-semibold text-sm">{otherUser?.name || "Utilisateur"}</h3>
                        {/* Status (fake for now) */}
                    </div>
                </div>
                {/* Actions like See Listing could go here */}
                {listingId && (
                    <Button variant="ghost" size="sm" asChild>
                        <Link href={`/annonces/${listingId}`}>Voir l'annonce</Link>
                    </Button>
                )}
            </div>

            {/* Messages Area */}
            <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-950/30 overflow-y-auto">
                <div className="space-y-4 min-h-[50vh]">
                    {messages.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground space-y-4">
                            <span className="text-4xl">👋</span>
                            <p>Dites bonjour pour commencer la conversation !</p>
                            <div className="flex gap-2">
                                {["Est-ce toujours disponible ?", "J'aimerais réserver !", "Bonjour !"].map(suggestions => (
                                    <Button
                                        key={suggestions}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => { setMessageText(suggestions); /* hack to focus */ }}
                                    >
                                        {suggestions}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((msg, idx) => {
                        const isOwn = msg.senderId === currentUser?.id;
                        const showAvatar = !isOwn && (idx === 0 || messages[idx - 1].senderId !== msg.senderId);

                        return (
                            <div
                                key={msg.id}
                                className={cn(
                                    "flex items-end gap-2 max-w-[85%]",
                                    isOwn ? "ml-auto flex-row-reverse" : "mr-auto"
                                )}
                            >
                                {!isOwn && (
                                    <div className="w-6 shrink-0">
                                        {showAvatar && (
                                            <Avatar className="h-6 w-6">
                                                <AvatarImage src={msg.sender.image || ""} />
                                                <AvatarFallback className="text-[10px]">{msg.sender.name?.[0]}</AvatarFallback>
                                            </Avatar>
                                        )}
                                    </div>
                                )}

                                <div className={cn(
                                    "rounded-2xl px-4 py-2 text-sm shadow-sm",
                                    isOwn
                                        ? "bg-primary text-primary-foreground rounded-br-none"
                                        : "bg-white dark:bg-slate-800 border rounded-bl-none"
                                )}>
                                    <p>{msg.content}</p>
                                    <p className={cn(
                                        "text-[10px] mt-1 text-right w-full block opacity-70",
                                        isOwn ? "text-primary-foreground" : "text-muted-foreground"
                                    )}>
                                        {formatDistanceToNow(new Date(msg.createdAt), { locale: fr, addSuffix: true })}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} className="h-1" />
                </div>
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-3 bg-background border-t">
                <div className="relative flex items-center gap-2">
                    <Button type="button" size="icon" variant="ghost" className="shrink-0 text-muted-foreground">
                        <ImageIcon className="h-5 w-5" />
                    </Button>
                    <Input
                        value={messageText}
                        onChange={e => setMessageText(e.target.value)}
                        placeholder="Écrivez votre message..."
                        className="rounded-full pr-12 bg-muted/30 border-muted-foreground/20 focus-visible:ring-primary"
                        disabled={sending}
                    />
                    <Button
                        type="submit"
                        size="icon"
                        disabled={!messageText.trim() || sending}
                        className={cn(
                            "absolute right-1 top-1 h-8 w-8 rounded-full transition-all",
                            messageText.trim() ? "opacity-100 scale-100" : "opacity-0 scale-90"
                        )}
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                </div>
            </form>
        </div>
    );
}
